import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsersIcon, BriefcaseIcon, ShieldIcon, CheckCircleIcon, XCircleIcon, ClockIcon, LogOutIcon, BarChart3Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
// Mock data for pending developers
const mockPendingDevelopers = [{
  id: '1',
  name: 'Alice Johnson',
  email: 'alice@example.com',
  title: 'Senior Laravel Developer',
  skills: ['Laravel', 'PHP', 'Vue.js', 'MySQL'],
  experience: '8 years',
  appliedDate: '2024-01-15',
  portfolio: 'https://alicejohnson.dev'
}, {
  id: '2',
  name: 'Bob Martinez',
  email: 'bob@example.com',
  title: 'Full Stack Developer',
  skills: ['Laravel', 'React', 'Node.js', 'PostgreSQL'],
  experience: '5 years',
  appliedDate: '2024-01-14',
  portfolio: 'https://bobmartinez.dev'
}, {
  id: '3',
  name: 'Carol Lee',
  email: 'carol@example.com',
  title: 'Backend Developer',
  skills: ['Laravel', 'PHP', 'MySQL', 'Redis'],
  experience: '6 years',
  appliedDate: '2024-01-13',
  portfolio: 'https://carollee.dev'
}];
// Mock data for pending jobs
const mockPendingJobs = [{
  id: '1',
  title: 'Senior Laravel Developer',
  company: 'TechCorp',
  location: 'Remote',
  salary: '$120k-$150k',
  postedDate: '2024-01-15',
  status: 'pending'
}, {
  id: '2',
  title: 'Full Stack Developer',
  company: 'StartupHub',
  location: 'New York, NY',
  salary: '$90k-$120k',
  postedDate: '2024-01-14',
  status: 'pending'
}];
// Mock data for admins
const mockAdmins = [{
  id: '1',
  name: 'John Admin',
  email: 'admin@certifieddevs.com',
  role: 'Super Admin',
  addedDate: '2023-01-01'
}, {
  id: '2',
  name: 'Sarah Manager',
  email: 'sarah@certifieddevs.com',
  role: 'Admin',
  addedDate: '2023-06-15'
}];
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [pendingDevelopers, setPendingDevelopers] = useState(mockPendingDevelopers);
  const [pendingJobs, setPendingJobs] = useState(mockPendingJobs);
  const [admins, setAdmins] = useState(mockAdmins);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  useEffect(() => {
    // Check if user is authenticated
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin/login');
    }
  }, [navigate]);
  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin/login');
  };
  const handleApproveDeveloper = (id: string) => {
    setPendingDevelopers(pendingDevelopers.filter(dev => dev.id !== id));
    // In production, this would call an API
  };
  const handleDenyDeveloper = (id: string) => {
    setPendingDevelopers(pendingDevelopers.filter(dev => dev.id !== id));
    // In production, this would call an API
  };
  const handleApproveJob = (id: string) => {
    setPendingJobs(pendingJobs.filter(job => job.id !== id));
    // In production, this would call an API
  };
  const handleDenyJob = (id: string) => {
    setPendingJobs(pendingJobs.filter(job => job.id !== id));
    // In production, this would call an API
  };
  const handleAddAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (newAdminEmail) {
      const newAdmin = {
        id: String(admins.length + 1),
        name: newAdminEmail.split('@')[0],
        email: newAdminEmail,
        role: 'Admin',
        addedDate: new Date().toISOString().split('T')[0]
      };
      setAdmins([...admins, newAdmin]);
      setNewAdminEmail('');
      // In production, this would call an API
    }
  };
  const handleRemoveAdmin = (id: string) => {
    setAdmins(admins.filter(admin => admin.id !== id));
    // In production, this would call an API
  };
  const tabs = [{
    id: 'overview',
    name: 'Overview',
    icon: BarChart3Icon
  }, {
    id: 'developers',
    name: 'Developers',
    icon: UsersIcon
  }, {
    id: 'jobs',
    name: 'Jobs',
    icon: BriefcaseIcon
  }, {
    id: 'admins',
    name: 'Admins',
    icon: ShieldIcon
  }];
  return <div className="min-h-screen bg-gray-50" data-id="element-178">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-179">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-180">
          <div className="flex justify-between items-center py-4" data-id="element-181">
            <div className="flex items-center" data-id="element-182">
              <ShieldIcon className="h-8 w-8 text-[#d73b3e] mr-3" data-id="element-183" />
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-184">
                Admin Dashboard
              </h1>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="flex items-center" data-id="element-185">
              <LogOutIcon className="h-5 w-5 mr-2" data-id="element-186" />
              Logout
            </Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-187">
        {/* Tabs */}
        <div className="mb-8" data-id="element-188">
          <div className="border-b border-gray-200" data-id="element-189">
            <nav className="-mb-px flex space-x-8" data-id="element-190">
              {tabs.map(tab => {
              const Icon = tab.icon;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`${activeTab === tab.id ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors`} data-id="element-191">
                    <Icon className="h-5 w-5 mr-2" data-id="element-192" />
                    {tab.name}
                  </button>;
            })}
            </nav>
          </div>
        </div>
        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-193">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-194">
              <Card className="bg-white" data-id="element-195">
                <div className="flex items-center justify-between" data-id="element-196">
                  <div data-id="element-197">
                    <p className="text-sm font-medium text-gray-600" data-id="element-198">
                      Pending Developers
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-199">
                      {pendingDevelopers.length}
                    </p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full" data-id="element-200">
                    <UsersIcon className="h-6 w-6 text-blue-600" data-id="element-201" />
                  </div>
                </div>
              </Card>
              <Card className="bg-white" data-id="element-202">
                <div className="flex items-center justify-between" data-id="element-203">
                  <div data-id="element-204">
                    <p className="text-sm font-medium text-gray-600" data-id="element-205">
                      Pending Jobs
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-206">
                      {pendingJobs.length}
                    </p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full" data-id="element-207">
                    <BriefcaseIcon className="h-6 w-6 text-green-600" data-id="element-208" />
                  </div>
                </div>
              </Card>
              <Card className="bg-white" data-id="element-209">
                <div className="flex items-center justify-between" data-id="element-210">
                  <div data-id="element-211">
                    <p className="text-sm font-medium text-gray-600" data-id="element-212">
                      Total Admins
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-213">
                      {admins.length}
                    </p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-full" data-id="element-214">
                    <ShieldIcon className="h-6 w-6 text-purple-600" data-id="element-215" />
                  </div>
                </div>
              </Card>
            </div>
            <Card className="bg-white" data-id="element-216">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-217">
                Recent Activity
              </h3>
              <div className="space-y-4" data-id="element-218">
                <div className="flex items-start" data-id="element-219">
                  <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-220">
                    <UsersIcon className="h-4 w-4 text-blue-600" data-id="element-221" />
                  </div>
                  <div className="flex-1" data-id="element-222">
                    <p className="text-sm text-gray-900" data-id="element-223">
                      New developer application from Alice Johnson
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-224">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-225">
                  <div className="bg-green-100 p-2 rounded-full mr-3" data-id="element-226">
                    <BriefcaseIcon className="h-4 w-4 text-green-600" data-id="element-227" />
                  </div>
                  <div className="flex-1" data-id="element-228">
                    <p className="text-sm text-gray-900" data-id="element-229">
                      New job posting from TechCorp
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-230">5 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-231">
                  <div className="bg-purple-100 p-2 rounded-full mr-3" data-id="element-232">
                    <CheckCircleIcon className="h-4 w-4 text-purple-600" data-id="element-233" />
                  </div>
                  <div className="flex-1" data-id="element-234">
                    <p className="text-sm text-gray-900" data-id="element-235">
                      Developer Bob Martinez was approved
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-236">1 day ago</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>}
        {/* Developers Tab */}
        {activeTab === 'developers' && <div data-id="element-237">
            <Card className="bg-white" data-id="element-238">
              <div className="mb-6" data-id="element-239">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-240">
                  Pending Developer Applications
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-241">
                  Review and approve developer applications
                </p>
              </div>
              {pendingDevelopers.length > 0 ? <div className="space-y-4" data-id="element-242">
                  {pendingDevelopers.map(developer => <div key={developer.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-243">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-244">
                        <div className="flex-1" data-id="element-245">
                          <div className="flex items-center mb-2" data-id="element-246">
                            <h4 className="text-lg font-semibold text-gray-900" data-id="element-247">
                              {developer.name}
                            </h4>
                            <span className="ml-3 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full" data-id="element-248">
                              Pending
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2" data-id="element-249">
                            {developer.title}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-250">
                            <span className="font-medium" data-id="element-251">Email:</span>{' '}
                            {developer.email}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-252">
                            <span className="font-medium" data-id="element-253">Experience:</span>{' '}
                            {developer.experience}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-2" data-id="element-254">
                            {developer.skills.map(skill => <span key={skill} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full" data-id="element-255">
                                {skill}
                              </span>)}
                          </div>
                          <p className="text-xs text-gray-400" data-id="element-256">
                            <ClockIcon className="h-3 w-3 inline mr-1" data-id="element-257" />
                            Applied on {developer.appliedDate}
                          </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-0 md:ml-4" data-id="element-258">
                          <Button variant="primary" size="sm" onClick={() => handleApproveDeveloper(developer.id)} className="flex items-center justify-center" data-id="element-259">
                            <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-260" />
                            Approve
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDenyDeveloper(developer.id)} className="flex items-center justify-center border-red-300 text-red-600 hover:bg-red-50" data-id="element-261">
                            <XCircleIcon className="h-4 w-4 mr-1" data-id="element-262" />
                            Deny
                          </Button>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-12" data-id="element-263">
                  <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-264" />
                  <p className="text-gray-600" data-id="element-265">
                    No pending developer applications
                  </p>
                </div>}
            </Card>
          </div>}
        {/* Jobs Tab */}
        {activeTab === 'jobs' && <div data-id="element-266">
            <Card className="bg-white" data-id="element-267">
              <div className="mb-6" data-id="element-268">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-269">
                  Pending Job Listings
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-270">
                  Review and approve job postings
                </p>
              </div>
              {pendingJobs.length > 0 ? <div className="space-y-4" data-id="element-271">
                  {pendingJobs.map(job => <div key={job.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-272">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-273">
                        <div className="flex-1" data-id="element-274">
                          <div className="flex items-center mb-2" data-id="element-275">
                            <h4 className="text-lg font-semibold text-gray-900" data-id="element-276">
                              {job.title}
                            </h4>
                            <span className="ml-3 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full" data-id="element-277">
                              Pending
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2" data-id="element-278">
                            {job.company}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-279">
                            <span className="font-medium" data-id="element-280">Location:</span>{' '}
                            {job.location}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-281">
                            <span className="font-medium" data-id="element-282">Salary:</span>{' '}
                            {job.salary}
                          </p>
                          <p className="text-xs text-gray-400" data-id="element-283">
                            <ClockIcon className="h-3 w-3 inline mr-1" data-id="element-284" />
                            Posted on {job.postedDate}
                          </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-0 md:ml-4" data-id="element-285">
                          <Button variant="primary" size="sm" onClick={() => handleApproveJob(job.id)} className="flex items-center justify-center" data-id="element-286">
                            <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-287" />
                            Approve
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDenyJob(job.id)} className="flex items-center justify-center border-red-300 text-red-600 hover:bg-red-50" data-id="element-288">
                            <XCircleIcon className="h-4 w-4 mr-1" data-id="element-289" />
                            Deny
                          </Button>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-12" data-id="element-290">
                  <BriefcaseIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-291" />
                  <p className="text-gray-600" data-id="element-292">No pending job listings</p>
                </div>}
            </Card>
          </div>}
        {/* Admins Tab */}
        {activeTab === 'admins' && <div className="space-y-6" data-id="element-293">
            <Card className="bg-white" data-id="element-294">
              <div className="mb-6" data-id="element-295">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-296">
                  Add New Admin
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-297">
                  Grant admin access to a new user
                </p>
              </div>
              <form onSubmit={handleAddAdmin} className="flex gap-4" data-id="element-298">
                <Input type="email" placeholder="admin@example.com" value={newAdminEmail} onChange={e => setNewAdminEmail(e.target.value)} className="flex-1" required data-id="element-299" />
                <Button type="submit" variant="primary" data-id="element-300">
                  Add Admin
                </Button>
              </form>
            </Card>
            <Card className="bg-white" data-id="element-301">
              <div className="mb-6" data-id="element-302">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-303">
                  Current Admins
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-304">
                  Manage admin access
                </p>
              </div>
              <div className="space-y-4" data-id="element-305">
                {admins.map(admin => <div key={admin.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-306">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-307">
                      <div className="flex-1" data-id="element-308">
                        <h4 className="text-lg font-semibold text-gray-900" data-id="element-309">
                          {admin.name}
                        </h4>
                        <p className="text-sm text-gray-600 mb-1" data-id="element-310">
                          {admin.email}
                        </p>
                        <div className="flex items-center gap-2" data-id="element-311">
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full" data-id="element-312">
                            {admin.role}
                          </span>
                          <span className="text-xs text-gray-400" data-id="element-313">
                            Added on {admin.addedDate}
                          </span>
                        </div>
                      </div>
                      {admin.role !== 'Super Admin' && <div className="mt-4 md:mt-0 md:ml-4" data-id="element-314">
                          <Button variant="outline" size="sm" onClick={() => handleRemoveAdmin(admin.id)} className="border-red-300 text-red-600 hover:bg-red-50" data-id="element-315">
                            Remove Access
                          </Button>
                        </div>}
                    </div>
                  </div>)}
              </div>
            </Card>
          </div>}
      </div>
    </div>;
};
export default AdminDashboard;